import { Stack, SxProps } from "@mui/material";
import { renderEventNameWithIdNumber } from "misc/renderHelpers";
import {
  AutocompleteInput,
  AutocompleteInputProps,
  RaRecord,
  ReferenceInput,
  required,
  useRecordContext,
  useTranslate,
} from "react-admin";

import { formatEventDateSpan } from "../../misc/formatHelpers";
import { EventRecordType } from "../../pages/Event/Event.types";
import EventChip from "../EventChip";

export type EventSelectInputPropsType = {
  onChange?: AutocompleteInputProps["onChange"];
  validate?: AutocompleteInputProps["validate"] | false;
  label?: string;
  eventBaseId?: number | undefined;
  filter?: Record<string, unknown>;
  perPage?: number;
  source?: string;
  reference?: string;
  defaultValue?: unknown;
  alwaysOn?: boolean;
  key?: string;
  sx?: SxProps;
};

export function EventSelectInputOption() {
  const record = useRecordContext<EventRecordType>();
  const translate = useTranslate();

  if (!record) {
    return null;
  }

  return (
    <Stack direction="row" gap={2}>
      <EventChip size="small" clickable={false} showStatus />
      {formatEventDateSpan(record, translate("no_date"))}
    </Stack>
  );
}

export default function EventSelectInput(props: EventSelectInputPropsType) {
  const {
    onChange,
    validate = required(),
    label = "resources.events.actions.existing_event",
    reference = "events",
    eventBaseId,
    filter = {},
    perPage = 100,
    source = "event",
    defaultValue,
    sx,
    ...rest
  } = props;

  const composedFilter = { ...(eventBaseId ? { order_by_event_base_id: eventBaseId } : {}), ...filter };
  const sort = {
    field: `${eventBaseId ? "event_base_id_ordering," : ""}event_base__name,-first_day`,
    order: "ASC" as const,
  };

  return (
    <ReferenceInput
      source={source}
      filter={composedFilter}
      reference={reference}
      sort={sort}
      filterToQuery={(search: string) => ({ search })}
      perPage={perPage}
      {...rest}
    >
      <AutocompleteInput
        parse={(v: string) => (v.length == 0 ? null : v)}
        inputText={(choice: EventRecordType) => `${renderEventNameWithIdNumber(choice)}`}
        optionText={<EventSelectInputOption />}
        onChange={onChange}
        fullWidth
        validate={validate ? validate : undefined}
        groupBy={(choice: RaRecord) => (choice as EventRecordType).meta.event_base_name}
        label={label}
        defaultValue={defaultValue}
        sx={sx}
      />
    </ReferenceInput>
  );
}
